/**
 * Split Pane State
 *
 * Pure helpers for opening, replacing, and closing split-pane targets.
 *
 * Primary responsibilities:
 * - Keep pane mutations deterministic and testable
 * - Preserve the remaining pane when closing one side
 *
 * Dependencies:
 * - lib/split-pane/targets
 */

import {
  getSplitPaneTargetKey,
  type SplitPaneId,
  type SplitPaneTarget
} from "@/lib/split-pane/targets"

export type SplitPaneState = Record<SplitPaneId, SplitPaneTarget | null>

export const emptySplitPaneState: SplitPaneState = {
  left: null,
  right: null
}

export const getOppositeSplitPane = (paneId: SplitPaneId): SplitPaneId =>
  paneId === "left" ? "right" : "left"

export const isSplitPaneMode = (panes: SplitPaneState) =>
  Boolean(panes.left && panes.right)

export function areSplitPaneTargetsEqual(
  left: SplitPaneTarget | null,
  right: SplitPaneTarget | null
) {
  if (!left || !right) return left === right

  return getSplitPaneTargetKey(left) === getSplitPaneTargetKey(right)
}

export function openSplitPaneTarget({
  currentRouteTarget,
  paneId,
  panes,
  target
}: {
  currentRouteTarget: SplitPaneTarget | null
  paneId: SplitPaneId
  panes: SplitPaneState
  target: SplitPaneTarget
}): SplitPaneState {
  if (isSplitPaneMode(panes)) {
    return {
      ...panes,
      [paneId]: target
    }
  }

  const currentTarget = panes.left ?? currentRouteTarget ?? target

  if (paneId === "left") {
    return {
      left: target,
      right: currentTarget
    }
  }

  return {
    left: currentTarget,
    right: target
  }
}

export function closeSplitPane({
  paneId,
  panes
}: {
  paneId: SplitPaneId
  panes: SplitPaneState
}): {
  panes: SplitPaneState
  remainingPaneId: SplitPaneId | null
  remainingTarget: SplitPaneTarget | null
} {
  const remainingPaneId = getOppositeSplitPane(paneId)
  const remainingTarget = panes[remainingPaneId]

  if (!remainingTarget) {
    return {
      panes: emptySplitPaneState,
      remainingPaneId: null,
      remainingTarget: null
    }
  }

  return {
    panes: {
      left: remainingTarget,
      right: null
    },
    remainingPaneId: "left",
    remainingTarget
  }
}
